// Astrea Ops Platform - Financials data-access layer (financials)
//
// Phase 3, eighth slice. Martin's explicit scope call, via AskUserQuestion (16
// September 2026): Financials only, not Proposal - the smallest, most contained of
// the remaining money-adjacent areas. `financials` is a single lump-sum object per
// project (contract value, deposit, site visit expenses, subcontractor invoices/
// payments, cadence travel/flights, Dataminr transfer, a final invoice, notes) -
// the schema's own header comment calls this table "the well-understood lump-sum
// fields Finance already works with today, low migration risk", and says real
// itemised invoicing (the separate invoices/payments tables) supersedes it over
// time rather than replacing it on day one. That's exactly the boundary this slice
// draws: `financials` gets normalized now; invoices/payments/change_orders/quotes/
// vendor_engagements/rate_cards/payout_lines/the proposals table itself all stay
// deferred, along with Proposal generally, as their own future slice(s).
//
// Deliberately NOT in scope this slice, same "deferred, not dropped, still readable
// via app_data" precedent as Blocked By (Tasks) and debrief owner (Event Form):
// `project.financialTracking` - a separate top-level object (not nested inside
// `financials`) holding a richer per-line-item status string (e.g. "Invoice Sent",
// "Payment Received") plus per-item notes. It has no matching column anywhere in
// the schema - `financials` only has a plain boolean paid flag per item, not an
// open-ended status - so financialTracking keeps living only in `projects.app_data`
// for now. The `deposit_paid`/`sv1_paid`/`sv2_paid`/`final_paid` booleans on
// `financials` DO get synced for real; they're the parts of financialTracking's
// state (isPaidFn() in FinancialsPanel reads financialTracking OR the paid flag)
// that already have a home.
//
// Schema gap found and fixed building this: makeTrainingTemplate() gives Training
// projects a financials shape with travelExpenses/materialsExpenses instead of the
// site-visit/subcontractor/cadence/Dataminr fields every other project type uses.
// `financials` has no jsonb catch-all the way `event_forms` does, so those two
// fields had nowhere to land - fixed in
// supabase/migrations/20260916000002_financials_training_expenses.sql (two new
// nullable numeric columns), same precedent as 20260915000010_venues_category.sql.
// With that fix, every project type's financials fields now map onto a real column -
// no jsonb bridge needed for this table at all.
//
// Architecture: same centralized-sync pattern as Tasks/Event Form.
// FinancialsPanel keeps the form in local useState and only pushes to the project
// on each field's onBlur (still going through the same onUpdate/updateProject
// funnel every other slice uses) - never once per keystroke. syncFinancialsChanges()
// is called once from updateProject, keyed on whether project.financials changed by
// reference.
//
// RLS: `financials` is already in the admin/finance-only policy bucket
// (is_admin_finance(), from 20260915000007_rls_policies.sql's financial_tables
// list) - matches FinancialsPanel's existing canSeeFinance() UI gate exactly, no
// new policy needed.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

// [appKey, dbColumn] - every field across every project type's financials shape
// maps onto a real column; there's no leftover bridge needed for this table.
const FINANCIALS_COLUMNS = [
  ['contractValue', 'contract_value'],
  ['deposit', 'deposit'],
  ['depositPaid', 'deposit_paid'],
  ['siteVisit1', 'site_visit1_amount'],
  ['sv1Paid', 'sv1_paid'],
  ['siteVisit2', 'site_visit2_amount'],
  ['sv2Paid', 'sv2_paid'],
  ['subcontractorInvoices', 'subcontractor_invoices'],
  ['subcontractorPayments', 'subcontractor_payments'],
  ['cadenceTravel', 'cadence_travel'],
  ['cadenceFlights', 'cadence_flights'],
  ['finalInvoice', 'final_invoice'],
  ['finalPaid', 'final_paid'],
  ['dataminrTransfer', 'dataminr_transfer'],
  ['travelExpenses', 'travel_expenses'],
  ['materialsExpenses', 'materials_expenses'],
  ['notes', 'notes'],
];
const NUMERIC_APP_KEYS = new Set([
  'contractValue', 'deposit', 'siteVisit1', 'siteVisit2', 'subcontractorInvoices',
  'subcontractorPayments', 'cadenceTravel', 'cadenceFlights', 'finalInvoice',
  'dataminrTransfer', 'travelExpenses', 'materialsExpenses',
]);
const BOOLEAN_APP_KEYS = new Set(['depositPaid', 'sv1Paid', 'sv2Paid', 'finalPaid']);

// One query across every project's financials row, grouped by project_id - same
// shape as unavailability.js/assignments.js.
export async function loadFinancialsByProject() {
  const { data, error } = await selectAllRes(() => supabase.from('financials').select('*').order('project_id'));
  if (error) throw error;
  return new Map((data || []).map(r => [r.project_id, r]));
}

// Falls back to appFinancials untouched if there's no real financials row yet (a
// project not touched since this slice landed, or a Prospect project that has no
// financials object at all). Real columns win for every mapped field.
export function mergeFinancials(appFinancials, realRow) {
  const base = appFinancials || {};
  if (!realRow) return base;
  const merged = { ...base };
  FINANCIALS_COLUMNS.forEach(([appKey, dbKey]) => {
    if (realRow[dbKey] !== null && realRow[dbKey] !== undefined) merged[appKey] = realRow[dbKey];
  });
  return merged;
}

export async function syncFinancialsChanges(projectId, financials) {
  if (!financials) return;
  const row = { project_id: projectId };
  FINANCIALS_COLUMNS.forEach(([appKey, dbKey]) => {
    const v = financials[appKey];
    if (NUMERIC_APP_KEYS.has(appKey)) row[dbKey] = v || 0;
    else if (BOOLEAN_APP_KEYS.has(appKey)) row[dbKey] = !!v;
    else row[dbKey] = v || null;
  });
  const { error } = await supabase.from('financials').upsert(row, { onConflict: 'project_id' });
  if (error) throw error;
}
